import { useUserIdentity } from '@src/stores-v2/StoreContext';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import { useFlagsCombination } from '@src/utility/useFlagsCombination';
import { observer } from 'mobx-react-lite';
import React from 'react';
import styled from 'styled-components';

import {
  generateSnapThreshold,
  getSnapResultForTime,
} from '../../utility/elementSnapping';
import { Draggable } from '../Draggable';
import { PADDING_LEFT_COMPENSATION } from './TimelineElement';

import type { ElementState } from '../../renderer/ElementState';
import type { ElementBoundary } from '../../utility/elementSnapping';
import type { DraggableEventHandler } from '../Draggable';

interface ResizeHandleProps {
  element: ElementState;
  side: 'start' | 'end';
  time: number;
  boundaries: ElementBoundary[];
  onChange: (time: number, trim_start: number, duration: number) => void;
  onComplete: () => void;
}

export const ResizeHandle: React.FC<ResizeHandleProps> = observer(props => {
  const videoCreator = useVideoCreatorStore();
  const timelineScale = videoCreator.timelineScale;

  const { enableTimelineSnapping } = useFlagsCombination(
    useUserIdentity().currentRole,
  );

  const onDrag: DraggableEventHandler = (e, data, context) => {
    e.preventDefault();

    let timeOffset = videoCreator.snapTime(
      (data.x - context.startX) / timelineScale,
    );

    if (props.side === 'start') {
      const proposedTime = props.element.localTime + timeOffset;
      let time = proposedTime;

      if (enableTimelineSnapping && context.snapBoundaries) {
        const snapResult = getSnapResultForTime(
          proposedTime,
          context.snapBoundaries,
          generateSnapThreshold(timelineScale),
        );

        if (snapResult.isWithinSnapWarningRange && snapResult.snapGuideTime) {
          videoCreator.setSnapGuide(true, snapResult.snapGuideTime);
        } else {
          videoCreator.setSnapGuide(false);
        }

        // within the threshold of a snap boundary
        if (snapResult.isWithinSnapRange && snapResult.snapTime) {
          // we want to ignore where the cursor is (timeOffset) and reset
          // timeOffset to what it would be if cursor was at the snap point
          timeOffset = snapResult.snapTime - proposedTime + timeOffset;
          time = snapResult.snapTime;
        }
      }
      let trimStart = (props.element.source.trim_start || 0) + timeOffset;
      let trimStartOffset = trimStart;
      let duration = props.element.duration - timeOffset;

      if (time < 0) {
        duration += time;
        trimStart -= time;
        time = 0;
      }

      if (
        ['audio', 'video'].includes(props.element.source.type) &&
        trimStart < 0
      ) {
        duration += trimStart;
        time -= trimStart;
        trimStart = 0;
      } else if (trimStart < 0) {
        trimStart = 0;
      }

      if (props.element.source.type !== 'audio') {
        props.onChange(
          videoCreator.snapTime(time),
          videoCreator.snapTime(trimStart),
          videoCreator.snapTime(duration),
        );
      } else {
        const diff = Math.abs(trimStartOffset - trimStart);
        if (diff === 0) {
          props.onChange(
            videoCreator.snapTime(time),
            videoCreator.snapTime(trimStart),
            videoCreator.snapTime(duration),
          );
        }
      }
    } else {
      const proposedTime =
        props.element.localTime + props.element.duration + timeOffset;
      let duration = props.element.duration + timeOffset;

      if (enableTimelineSnapping && context.snapBoundaries) {
        const snapResult = getSnapResultForTime(
          proposedTime,
          context.snapBoundaries,
          generateSnapThreshold(timelineScale),
        );
        if (snapResult.isWithinSnapWarningRange) {
          videoCreator.setSnapGuide(true, snapResult.snapTime);
        } else {
          videoCreator.setSnapGuide(false);
        }

        // within the threshold of a snap boundary
        if (snapResult.isWithinSnapRange && snapResult.snapTime) {
          // we want to ignore where the cursor is (timeOffset) and reset
          // timeOffset to what it would be if cursor was at the snap point
          duration = duration + (snapResult.snapTime - proposedTime);
        }
      }

      duration = Math.max(videoCreator.snapTime(duration), 0.5);

      props.onChange(
        videoCreator.snapTime(props.element.localTime),
        props.element.trimStart,
        videoCreator.snapTime(duration),
      );
    }
  };

  return (
    <Draggable
      cancel={'.volume-key-point'}
      onStart={(e, data) => {
        e.preventDefault();
        // Pre-calculate snap boundaries for efficient snapping during drag
        const snapBoundaries = videoCreator.createSnapBoundariesForDrag([
          props.element.source.id,
        ]);

        return {
          startX: data.x,
          snapBoundaries,
          originalDuration: props.element.duration,
        };
      }}
      onDrag={onDrag}
      onStop={() => {
        videoCreator.setSnapGuide(false);
        props.onComplete();
      }}
    >
      {ref => (
        <Main
          ref={ref}
          style={{
            left:
              PADDING_LEFT_COMPENSATION +
              props.time * timelineScale -
              (props.side === 'end' ? 7 : 0),
          }}
        ></Main>
      )}
    </Draggable>
  );
});

const Main = styled.div`
  position: absolute;
  cursor: ew-resize;
  z-index: 1;
  left: 0px;
  width: 7px;
  height: 100%;
  cursor: ew-resize;
`;
